import {HondaOfferBuilder} from '@muvio/honda-offers-builder'
import {RejectionReason} from '@muvio/honda-offers-builder/model'
import {computed, ref} from '@vue/composition-api'

import apiOffers from '@/api/offers.api'
import {useGtm} from '@/plugins/gtm'
import {Offer, Status} from '@/types/offers'

interface Options {
  dealerId: string
  onChanged?: () => void
  router?: any
}

export function useOfferChangeStatus({dealerId, onChanged, router}: Options) {
  const gtm = useGtm()
  const loading = ref(false)
  const offerToReject = ref<Offer | null>(null)
  const showRejectDialog = computed(() => offerToReject.value !== null)

  async function changeStatus(offer: Offer, status: Status) {
    if (loading.value) return

    if (status === Status.Refused && offer.status === Status.Accepted) {
      offerToReject.value = offer
      return
    }

    gtm?.trackEvent({
      category: 'Offers',
      action: 'EditStatusOffer',
      label: `${offer.id}-${status}`,
    })

    try {
      loading.value = true
      const fullOffer = await apiOffers.getOfferById(offer.id || '', dealerId)
      const newOffer = new HondaOfferBuilder().fromOffer(fullOffer.data)
      newOffer.status = status

      await apiOffers.editOffer(offer.id || '', newOffer.toOffer(), dealerId)
      offer.status = status
      ;(offer as any).updatedStatusFromEmail = false
      onChanged?.()
    } catch (err) {
      console.error(err)
    } finally {
      loading.value = false
    }
  }

  async function onRejectConfirm(payload: {rejectionReason: RejectionReason; rejectionReasonCustom?: string}) {
    const offer = offerToReject.value
    if (!offer?.id) return

    gtm?.trackEvent({
      category: 'Offers',
      action: 'EditStatusOffer',
      label: `${offer.id}-${Status.Refused}-${payload.rejectionReason}`,
    })

    try {
      loading.value = true
      await apiOffers.rejectOfferAsDealer(offer.id, dealerId, payload)
      const rejectedOfferId = offer.id
      offerToReject.value = null
      onChanged?.()
      router?.push({name: 'ResumeOfferMotorbike', params: {id: rejectedOfferId}})
    } catch (err) {
      console.error(err)
    } finally {
      loading.value = false
    }
  }

  function onRejectClose() {
    offerToReject.value = null
  }

  return {
    loading,
    showRejectDialog,
    changeStatus,
    onRejectConfirm,
    onRejectClose,
  }
}
